/**
 * CSV Export Utilities
 * Centralized utilities for CSV generation with proper encoding and human-readable headers.
 * Adapted from fast-cortex-orderbook: the job-costing column map is not carried —
 * drill-table headers are added per drill contract (S3) as the registry defines them.
 */

/**
 * Column header mapping — technical field names → human-readable labels.
 * Uses British English (e.g., "Labour" not "Labor"). Populated per drill contract.
 */
export const CSV_COLUMN_HEADERS = {}

/**
 * Generate CSV content with UTF-8 BOM to prevent encoding issues
 * @param {Array<Array<string>>} rows - Array of rows, each row is an array of cell values
 * @param {Object} options - Generation options
 * @param {boolean} options.includeBOM - Whether to include UTF-8 BOM (default: true)
 * @returns {string} CSV content with proper encoding
 */
export const generateCSV = (rows, options = {}) => {
  const { includeBOM = true } = options

  // Join rows into CSV format
  const csvContent = rows.map(row => row.join(',')).join('\n')

  // Add UTF-8 BOM to ensure proper encoding in Excel and other programs
  // This prevents £ and other special characters from appearing as ^ or other artifacts
  const BOM = '\uFEFF'
  const finalContent = includeBOM ? BOM + csvContent : csvContent

  return finalContent
}

/**
 * Download CSV file with proper encoding
 * @param {string} csvContent - CSV content to download
 * @param {string} filename - Filename for the download
 */
export const downloadCSV = (csvContent, filename) => {
  // Create blob with explicit UTF-8 charset
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)

  link.setAttribute('href', url)
  link.setAttribute('download', filename)
  link.style.visibility = 'hidden'

  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)

  // Clean up the URL object
  URL.revokeObjectURL(url)
}

/**
 * Map technical header names to human-readable labels
 * @param {Array<string>} headers - Array of technical header names
 * @returns {Array<string>} Array of human-readable header labels
 */
export const mapHeaders = (headers) => {
  return headers.map(h => CSV_COLUMN_HEADERS[h] || h)
}

/**
 * Format a number for CSV export with smart decimal handling
 * - Rounds to 2 decimal places
 * - Removes trailing zeros and decimal point for whole numbers
 * - Examples: 1040.00 → "1040", 1040.37287434 → "1040.37", 1040.5 → "1040.5"
 * @param {number|null|undefined} value - Number to format
 * @returns {string} Formatted number string
 */
export const formatNumberForCSV = (value) => {
  if (value == null || isNaN(value)) {
    return ''
  }

  // Round to 2 decimal places
  const rounded = Math.round(value * 100) / 100

  // Convert to string with up to 2 decimal places, then remove trailing zeros
  const formatted = rounded.toFixed(2).replace(/\.?0+$/, '')

  return formatted
}
